import { ConnectError } from '@bufbuild/connect';
import {
  DocumentViewer,
  IStyles,
  LinkButton,
  deserify,
  getMediaBasePath,
  useCommonServiceClientContext,
  useFeatureEnabled,
  ReportButton,
} from '@geneo2-web/shared-ui';
import { Box } from '@mui/material';
import { FileEnum } from '@protos/content_management/content.db_pb';
import { Feature } from '@protos/school_management/school.db_pb';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useGlobalContext } from '../../../app/Context/GlobalContextProvider';
import { useAppSelector } from '../../../reduxStore/reduxHooks';
import { setToastInfo } from '../../Home/reducer/homeDashboard.slice';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
  AttemptTypeEnum,
  ModeOfConductionEnum,
} from '@protos/learning_management/lms.db_pb';
import { interactionEvent } from '../../Auth/auth.events';
import { useDownloadContext } from '../../../app/Context/DownloadContextProviderV2';

const styles: IStyles = {};

interface IViewReport {
  homeworkId: bigint;
  studentId?: bigint;
  isMultiSubjectAssessment?: boolean;
  isJeeReport?: boolean;
  subject?: string;
  className?: string;
  section?: string;
  modeOfConduction?: ModeOfConductionEnum;
  omrStudentId?: string | null | undefined;
}

export default function ViewStudentReportButton(props: IViewReport) {
  const location = useLocation();
  const pathSegments = location.pathname.split('/');
  const { isOffline } = useDownloadContext();
  const {
    SchoolAssessmentAPIServiceV3ClientWithStatusCodeHandler,
    SchoolAssessmentAPIServiceV2ClientWithStatusCodeHandler,
    SchoolAssessmentAPIServiceV1ClientWithStatusCodeHandler,
  } = useCommonServiceClientContext();
  const user = deserify(useAppSelector((state) => state.auth.user_info));
  const featureInfo = user?.schoolDetails[0]?.featuresPlanInfo;
  const isViewReportsEnabled = useFeatureEnabled(
    featureInfo,
    Feature.VIEW_REPORTS
  );
  const isOmrAssessment =
    props.modeOfConduction ===
    ModeOfConductionEnum.MODE_OF_CONDUCTION_GENEO_OMR;
  const isSummaryReportsEnabled =
    !props.isMultiSubjectAssessment &&
    !props.isJeeReport &&
    !isOmrAssessment &&
    useFeatureEnabled(featureInfo, Feature.SUMMARIZED_REPORTS);
  const { setShowAppLoader } = useGlobalContext();
  const [pdfUrl, setPdfUrl] = useState<string | undefined>(undefined);
  const [isSummaryPdf, setIsSummaryPdf] = useState(false);
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const attemptNumber = searchParams.get('attemptNumber');
  const isBestAttempt = searchParams.get('isBestAttempt');

  if (!isViewReportsEnabled) {
    return null;
  }

  const onDownloadClick = async (isSummaryReport?: boolean) => {
    try {
      setShowAppLoader(true);
      let reportGen = false;
      let msg = '';
      if (isSummaryReport) {
        const res =
          await SchoolAssessmentAPIServiceV2ClientWithStatusCodeHandler.fetchStudentAssessmentSummaryReport(
            {
              assessmentId: props.homeworkId,
              studentId: props.studentId,
              attemptNumber: isBestAttempt
                ? undefined
                : attemptNumber
                ? Number(attemptNumber)
                : undefined,
              attemptType: isBestAttempt
                ? AttemptTypeEnum.ATTEMPT_TYPE_BEST
                : undefined,
            }
          );
        if (res.data?.gcpSummaryReportUrl) {
          reportGen = true;
          setIsSummaryPdf(true);
          setPdfUrl(getMediaBasePath(res.data?.gcpSummaryReportUrl));
        } else {
          msg = res.message;
        }
      } else {
        let response;
        if (
          props.modeOfConduction ===
          ModeOfConductionEnum.MODE_OF_CONDUCTION_GENEO_OMR
        ) {
          response =
            await SchoolAssessmentAPIServiceV1ClientWithStatusCodeHandler.fetchStudentOMRKdtAssessmentReport(
              {
                assessmentId: props.homeworkId,
                studentId: props.omrStudentId ?? '', // OMR has special student id
              }
            );
        } else {
          response =
            await SchoolAssessmentAPIServiceV3ClientWithStatusCodeHandler.fetchStudentAssessmentReport(
              {
                assessmentId: props.homeworkId,
                studentId: props.studentId,
                attemptNumber: isBestAttempt
                  ? undefined
                  : attemptNumber
                  ? Number(attemptNumber)
                  : undefined,
                attemptType: isBestAttempt
                  ? AttemptTypeEnum.ATTEMPT_TYPE_BEST
                  : undefined,
              }
            );
        }

        if (response.data?.gcpReportUrl) {
          reportGen = true;
          setIsSummaryPdf(false);
          setPdfUrl(getMediaBasePath(response.data?.gcpReportUrl));
        } else {
          msg = response.message;
        }
      }
      if (!reportGen) {
        dispatch(
          setToastInfo({
            label: msg,
            variant: 'success',
            open: true,
          })
        );
      }
      setShowAppLoader(false);
    } catch (err) {
      if (err instanceof ConnectError) {
        console.log('Error:', err.rawMessage);
        if (
          err.rawMessage.includes(
            'Unable to generate the assessment report as homework has not been submitted'
          )
        ) {
          dispatch(
            setToastInfo({
              label: 'Assessment has not been submitted.',
              variant: 'error',
              open: true,
            })
          );
        } else {
          dispatch(
            setToastInfo({
              label: err.rawMessage,
              variant: 'error',
              open: true,
            })
          );
        }
      } else {
        dispatch(
          setToastInfo({
            label: 'Error while generating report',
            variant: 'error',
            open: true,
          })
        );
      }
      setShowAppLoader(false);
    }
  };

  const buttonList = [
    {
      name: 'Detailed Report',
      onClick: async () => {
        onDownloadClick(false);
        await interactionEvent({
          context: 'Your_Assessment',
          name: 'STUDENT_DETAILED_REPORT',
          pathSegments: pathSegments,
          isOffline: isOffline,
        });
      },
    },
  ];

  if (isSummaryReportsEnabled) {
    buttonList.push({
      name: 'Summary Report',
      onClick: async () => {
        onDownloadClick(true);
        await interactionEvent({
          context: 'Your_Assessment',
          name: 'STUDENT_SUMMARY_REPORT',
          pathSegments: pathSegments,
          isOffline: isOffline,
        });
      },
    });
  }

  return (
    <>
      <Box
        sx={{ display: 'flex', justifyContent: 'center', marginTop: '10px' }}
      >
        <ReportButton buttonText="Student Report" buttonList={buttonList} />
      </Box>
      {pdfUrl && (
        <DocumentViewer
          downloadUrl={pdfUrl}
          previewUrl={pdfUrl}
          gcpUrl={pdfUrl}
          type={FileEnum.FILE_TYPE_PDF}
          fileName={
            props.subject || props.className || props.section
              ? `${props.subject || ''}_${props.className || ''}_${
                  props.section || ''
                }_Geneo_Assessment_${
                  isSummaryPdf ? 'Summary_Report' : 'Report'
                }_Student_${props.studentId}_${props.homeworkId}.pdf`
              : `Geneo_Assessment_${
                  isSummaryPdf ? 'Summary_Report' : 'Report'
                }_Student_${props.studentId}_${props.homeworkId}.pdf`
          }
          open={!!pdfUrl}
          closeFile={() => {
            setPdfUrl(undefined);
            setIsSummaryPdf(false);
          }}
        />
      )}
    </>
  );
}
